import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Avatar } from "@/components/PhotoUpload";
import { Users, Crown, TrendingUp, CheckCircle2, XCircle, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * TeamLeadWidget — Fase 2 Team Lead
 * Shows aggregate stats of the team + drill-down list of members.
 * Only fetched/shown when the current user is linked to a player with
 * is_team_lead=True (backend returns 403/400 otherwise, we hide silently).
 */
export default function TeamLeadWidget() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/team-lead/summary")
      .then((r) => setData(r.data))
      .catch(() => setErr("not_lead"));
  }, []);

  if (err || !data) return null;

  const { team, members = [], aggregate = {} } = data;
  const reported = aggregate.reported_this_week || 0;
  const notReported = aggregate.not_reported_this_week || 0;
  const totalTeam = reported + notReported;
  const reportedPct = totalTeam > 0 ? Math.round((100 * reported) / totalTeam) : 0;

  return (
    <div className="kw-card p-0 overflow-hidden" data-testid="team-lead-widget">
      <div className="p-5 border-b border-[#222] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="kw-overline flex items-center gap-2">
            <Crown className="w-3 h-3 text-[#EAB308]" /> Team Lead · {team?.name}
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
            A minha <span className="text-[#EAB308]">Equipa</span>
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-1">
            Edição actual · Semana {data.current_week} · {team?.size} {team?.size === 1 ? "membro" : "membros"}
          </p>
        </div>
        <Link to="/rankings" className="kw-btn-secondary text-xs inline-flex items-center gap-2" data-testid="team-lead-open-rankings">
          Ver Rankings <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Aggregate KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-[#222]" data-testid="team-lead-kpis">
        <KPI label="Pontos (equipa · edição)" value={Math.round(aggregate.total_points || 0)} color="#E50000" />
        <KPI label="Vendas (equipa · edição)" value={aggregate.totals?.vendas || 0} color="#EAB308" />
        <KPI label="Reuniões (equipa · edição)" value={aggregate.totals?.reunioes || 0} color="#3B82F6" />
        <KPI
          label="Reportou esta semana"
          value={`${reported}/${totalTeam}`}
          hint={`${reportedPct}%`}
          color={reportedPct >= 80 ? "#22C55E" : reportedPct >= 50 ? "#EAB308" : "#E50000"}
        />
      </div>

      {/* Members table */}
      <div className="p-5">
        <div className="kw-overline mb-3">Membros</div>
        <div className="space-y-2" data-testid="team-lead-members">
          {members.map((m) => (
            <MemberRow key={m.player_id} m={m} />
          ))}
          {members.length === 0 && (
            <div className="text-sm text-[#737373] italic">Sem membros nesta equipa.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, hint, color = "#E50000" }) {
  return (
    <div className="p-4 border-r border-[#222] last:border-r-0" data-testid={`team-lead-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="kw-overline text-[10px]">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="font-display text-3xl font-black" style={{ color }}>{value}</div>
        {hint && <div className="text-xs text-[#A3A3A3] font-bold">{hint}</div>}
      </div>
    </div>
  );
}

function MemberRow({ m }) {
  const isAssistant = m.role_in_team === "assistant";
  const isBench = m.on_bench;
  return (
    <div
      className="grid grid-cols-12 gap-3 items-center py-2 border-b border-[#1A1A1A] hover:bg-[#141414] transition-colors"
      data-testid={`team-lead-member-${m.player_id}`}
    >
      <div className="col-span-5 md:col-span-4 flex items-center gap-3 min-w-0">
        <Avatar photoUrl={m.photo_url} name={m.name} size={36} />
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">
            {m.name}
            {m.is_team_lead && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-[#EAB308] font-bold" title="Team Lead">
                <Crown className="w-2.5 h-2.5" /> Lead
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold">
            {isAssistant ? "Assistente" : m.mbti || "—"}
            {isBench && <span className="ml-2 text-[#A3A3A3]">· Fora edição</span>}
          </div>
        </div>
      </div>
      <div className="col-span-3 md:col-span-2 text-right">
        <div className="font-display text-lg font-black">{Math.round(m.total_points || 0)}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">Pontos</div>
      </div>
      <div className="hidden md:block col-span-3 text-xs text-[#A3A3A3] font-mono">
        {m.totals?.vendas || 0}V · {m.totals?.angariacoes || 0}A · {m.totals?.reunioes || 0}R · {m.totals?.contactos || 0}C
      </div>
      <div className="col-span-4 md:col-span-3 text-right flex items-center justify-end gap-2">
        {m.reported_this_week ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#22C55E] font-bold" data-testid={`member-status-${m.player_id}`}>
            <CheckCircle2 className="w-3 h-3" /> Reportou
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#E50000] font-bold" data-testid={`member-status-${m.player_id}`}>
            <XCircle className="w-3 h-3" /> Falta
          </span>
        )}
      </div>
    </div>
  );
}
